import { put, select, takeLatest } from 'redux-saga/effects';
import fp from 'lodash/fp';
import { i18n } from '@factorin/container.macro';

import { messagingActions } from 'Modules/core/messaging';
import { getCertsList, signHash } from 'Modules/business/cryptopro';

import { certificatesActions, certificatesActionTypes } from './actions';
import { certificateFilters } from './filters';
import { allowedOids, hashAlgorithmMap } from '../../constants';


export function* notifyCryptoProError(preamble, error) {
    yield put(messagingActions.showNotification({
        content: {
            key: 'notifications:cryptoprofiles.errorConstructor',
            values: { error },
            translatedValues: { preamble },
        },
    }));
}

const processCertificate = fp.pick(['thumbprint', 'name', 'validTo', 'oid']);

export function* getCertificatesAllowedList(filterType) {
    const certs = yield getCertsList();
    const registered = yield select(({ cryptoprofiles }) => cryptoprofiles.profiles);

    for (let i = 0; i < certs.length; i++) {
        const alg = yield certs[i].getAlgorithm();
        certs[i].oid = alg.oid;
    }

    return fp.map(cert => {
        const filterResult = certificateFilters[filterType](cert, registered);
        return {
            disabled: filterResult !== null,
            ...filterResult && { reason: filterResult },
            ...processCertificate(cert),
        };
    }, certs);
}

export function* tryUpdateCertificatesList(filterType, withoutPreSelect = false) {
    const allowedCerts = yield getCertificatesAllowedList(filterType);
    yield put(certificatesActions.setList(filterType, allowedCerts));

    if (!withoutPreSelect) {
        const selected = yield select(fp.get(`certificates[${filterType}].selected`));
        if (!selected) {
            const availableCerts = fp.reject(fp.get('disabled'), allowedCerts);
            if (availableCerts.length === 1) {
                yield put(certificatesActions.setCertificate(filterType, {
                    cert: availableCerts[0].thumbprint,
                }));
            }
        }
    }
}

export function* updateCertificateList({ path, $payload }) {
    try {
        yield tryUpdateCertificatesList(path, $payload.withoutPreSelect);
    } catch ({ message }) {
        yield put(certificatesActions.setError(path, message));
        yield notifyCryptoProError('notifications:cryptoprofiles.errorRefresh', message);
    }
}

const SIGNING_RETIES = 3;

const getSelectedCertificate = path => ({ certificates }) => fp.find(
    cert => cert.thumbprint === certificates[path].selected,
    certificates[path].available,
);

/** @type {(path: string) => string} */
export function* getSelectedCertificateAlgorithmEnum(path) {
    const cert = yield select(getSelectedCertificate(path));
    return hashAlgorithmMap[cert.oid];
}

/** @type {(path: string, data: string) => string} */
export function* signWithSelectedCertificate(path, data) {
    const cert = yield select(getSelectedCertificate(path));
    const alg = allowedOids[cert.oid];

    if (!alg)
        throw new Error(i18n.t('notifications:cryptoprofiles.errorSigning'));

    for (let i = 0; i < SIGNING_RETIES; i++) {
        const signature = yield signHash(cert.thumbprint, data, alg);
        if (signature)
            return signature;
    }

    throw new Error(i18n.t('notifications:cryptoprofiles.errorSigning'));
}

export const sagas = [
    takeLatest(certificatesActionTypes.UPDATE_LIST, updateCertificateList),
];
